(function HotelEngineSortHotels($, Adzuki)
{
	'use strict';

	const SORT_OPTIONS = new Set([
		'topPicks',
		'priceAsc',
		'priceDesc',
		'ratingDesc',
		'ratingAsc',
		'distanceAsc',
		'distanceDesc',
	]);

	const component = {
		emits: ['sorted-hotels', 'sort-changed'],
		props: {
			visibleHotels: {
				type    : Array,
				required: false,
				default : () => []
			},
			hotels: {
				type    : Array,
				required: false,
				default : () => []
			},
			initialSort: {
				type   : String,
				default: 'topPicks',
			},
		},
		template: `
			<div class="form-inline">
				<div class="form-group">
					<label for="hotel-sort-input" class="control-label margin5-right">Sort By: </label>
					<select id="hotel-sort-input" v-model="selectedSortOption" @change="onUserSortChange" class="form-control hotel-sort-select">
						<option value="topPicks">Top Picks</option>
						<option value="priceAsc">Price: Low to High</option>
						<option value="priceDesc">Price: High to Low</option>
						<option value="ratingDesc">Stars: High to Low</option>
						<option value="ratingAsc">Stars: Low to High</option>
						<option value="distanceAsc">Distance: Closest to Furthest </option>
						<option value="distanceDesc">Distance: Furthest to Closest </option>
					</select>
				</div>
			</div>
		`,
		data() {
			return {
				selectedSortOption : 'topPicks',
				originalSort       : [],
				initialSortApplied : false,
				userChangedSort    : false,
				skipNextSourceWatch: false,
			};
		},
		computed: {
			sourceHotels() {
				if (Array.isArray(this.hotels) && this.hotels.length) {
					return this.hotels;
				}
				return Array.isArray(this.visibleHotels) ? this.visibleHotels : [];
			}
		},
		methods: {
			isValidSortOption(value) {
				return typeof value === 'string' && SORT_OPTIONS.has(value);
			},
			toNumber(value) {
				if (typeof value === 'number') {
					return Number.isFinite(value) ? value : 0;
				}

				if (typeof value === 'string') {
					const normalized = value.replace(/[^0-9.-]/g, '');
					const parsed = parseFloat(normalized);
					return Number.isFinite(parsed) ? parsed : 0;
				}

				return 0;
			},
			getSortablePrice(hotel) {
				const pricing = hotel?.hotel_pricing || {};
				const rawRate = pricing.price_per_night
					?? pricing.room_price_per_night
					?? pricing.best_search_rate
					?? 0;

				return this.toNumber(rawRate);
			},
			hasSellingPrice(hotel) {
				return this.toNumber(hotel?.hotel_pricing?.selling_price) > 0;
			},
			moveUnpricedToBottom(hotels) {
				const list = Array.isArray(hotels) ? hotels : [];
				const priced = [];
				const unpriced = [];

				list.forEach((hotel) => {
					if (this.hasSellingPrice(hotel)) {
						priced.push(hotel);
					} else {
						unpriced.push(hotel);
					}
				});

				return [...priced, ...unpriced];
			},
			onUserSortChange() {
				this.userChangedSort = true;
				this.sortHotels();
			},
			applyInitialSortIfNeeded() {
				if (this.userChangedSort || this.initialSortApplied || !this.sourceHotels.length) {
					return;
				}

				const sortToApply = this.isValidSortOption(this.initialSort)
					? this.initialSort
					: 'topPicks';

				this.selectedSortOption = sortToApply;
				this.initialSortApplied = true;
				this.sortHotels();
			},
			resetSort() {
				this.userChangedSort    = false;
				this.initialSortApplied = false;
				this.selectedSortOption = 'topPicks';
				this.originalSort       = this.moveUnpricedToBottom([...this.sourceHotels]);
				this.$emit('sort-changed', 'topPicks');

				if (this.sourceHotels.length) {
					this.$emit('sorted-hotels', [...this.originalSort]);
				}
			},
			sortHotels() {
				let sortedHotels = [];

				if (!this.selectedSortOption) {
					return;
				}

				if (this.selectedSortOption === 'topPicks') {
					sortedHotels = [...this.originalSort];
				} else {
					sortedHotels = [...this.sourceHotels].sort((a, b) => {
						const hasPriceA = this.hasSellingPrice(a);
						const hasPriceB = this.hasSellingPrice(b);

						// Always keep hotels without selling_price after priced hotels.
						if (hasPriceA !== hasPriceB) {
							return hasPriceA ? -1 : 1;
						}

						const priceA    = this.getSortablePrice(a);
						const priceB    = this.getSortablePrice(b);
						const ratingA   = this.toNumber(a.star_rating);
						const ratingB   = this.toNumber(b.star_rating);
						const distanceA = this.toNumber(a.distance);
						const distanceB = this.toNumber(b.distance);

						switch (this.selectedSortOption) {
							case 'priceAsc':
								return priceA - priceB;
							case 'priceDesc':
								return priceB - priceA;
							case 'ratingDesc':
								return ratingB - ratingA;
							case 'ratingAsc':
								return ratingA - ratingB;
							case 'distanceAsc':
								return distanceA - distanceB;
							case 'distanceDesc':
								return distanceB - distanceA;
							default:
								return 0;
						}
					});
				}

				sortedHotels = this.moveUnpricedToBottom(sortedHotels);

				this.$emit('sorted-hotels', sortedHotels);
				this.$emit('sort-changed', this.selectedSortOption);
				this.skipNextSourceWatch = true;
			},
			handleSourceHotelsChange(newVal) {
				if (!Array.isArray(newVal) || !newVal.length) {
					return;
				}

				if (this.skipNextSourceWatch) {
					this.skipNextSourceWatch = false;

					if (this.selectedSortOption === 'topPicks') {
						this.originalSort = this.moveUnpricedToBottom([...newVal]);
					}

					return;
				}

				if (this.selectedSortOption === 'topPicks') {
					this.originalSort = this.moveUnpricedToBottom([...newVal]);
				}

				if (!this.initialSortApplied && !this.userChangedSort) {
					this.applyInitialSortIfNeeded();
					return;
				}

				// Re-apply sort (or top-picks order) so unpriced hotels stay at the bottom
				// after filter updates as well as explicit sort changes.
				this.sortHotels();
			},
		},
		watch: {
			sourceHotels: {
				immediate: true,
				handler(newVal) {
					this.handleSourceHotelsChange(newVal);
				}
			}
		}
	};

	Adzuki.VueComponents.HotelEngineSortHotels = component;
}(jQuery, window.Adzuki || {}));
